import React, { useState, useEffect } from 'react';
import Dashboard from './Dashboard';
import Sidebar from './Sidebar';
import CompanyDetail from './CompanyDetail';
import OverviewPage from './OverviewPage';
import Footer from './Footer';
import companiesData from '../data/companies.json';
import { useGlobalData } from '../context/GlobalDataContext';
import './LandingPage.css';

const LandingPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [activeView, setActiveView] = useState('overview');
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const { preloadLazyData, loadTimeSeriesData } = useGlobalData();

  const tabs = [
    { id: 'overview', label: 'Overview', mobileLabel: 'Overview', color: '#10b981', accentColor: '#34d399' },
    { id: 'bitcoin', label: 'Bitcoin', mobileLabel: 'BTC', color: '#f7931a', accentColor: '#ff9500' },
    { id: 'ethereum', label: 'Ethereum', mobileLabel: 'ETH', color: '#627eea', accentColor: '#4da6ff' },
    { id: 'solana', label: 'Solana', mobileLabel: 'SOL', color: '#9945ff', accentColor: '#9945ff' }
  ];

  const handleNavigateToCompany = (ticker) => {
    // Find the company by ticker across all asset types (only tracked companies)
    const allCompanies = [
      ...companiesData.bitcoin.filter(c => c.trackData === true),
      ...companiesData.ethereum.filter(c => c.trackData === true),
      ...companiesData.solana.filter(c => c.trackData === true)
    ];
    const company = allCompanies.find(c => c.ticker === ticker);
    
    if (company) {
      // Determine the asset type based on which array the company is in
      let assetType = 'bitcoin';
      if (companiesData.ethereum.includes(company)) {
        assetType = 'ethereum';
      } else if (companiesData.solana.includes(company)) {
        assetType = 'solana';
      }
      
      // Set the active tab to the asset type and active view to the company ID
      setActiveTab(assetType);
      setActiveView(company.id);
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleViewChange = (view) => {
    setActiveView(view);
    // Scroll content area to top
    const contentArea = document.querySelector('.content-area');
    if (contentArea) {
      contentArea.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setActiveView('overview'); // Reset to overview when switching tabs
    // Scroll content area to top
    const contentArea = document.querySelector('.content-area');
    if (contentArea) {
      contentArea.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // Trigger lazy data preload when switching to a different tab
    // (if not already triggered - it checks internally)
    preloadLazyData();
    
    // Load time series data for this specific asset (bitcoin, ethereum, solana)
    if (tabId !== 'overview') {
      loadTimeSeriesData(tabId);
    }
  };

  const renderContent = () => {
    // Show overview page when overview tab is active
    if (activeTab === 'overview') {
      return (
        <div className="content-area full-width">
          <OverviewPage onNavigateToAsset={handleTabChange} onNavigateToCompany={handleNavigateToCompany} />
          <Footer />
        </div>
      );
    }

    // For crypto tabs (bitcoin, ethereum, solana)
    if (activeView === 'overview') {
      return (
        <div className="content-area">
          <Dashboard cryptoType={activeTab} />
          <Footer />
        </div>
      );
    }
    
    // Find the selected company (only tracked companies)
    const companies = (companiesData[activeTab] || []).filter(company => company.trackData === true);
    const selectedCompany = companies.find(c => c.id === activeView);
    
    if (selectedCompany) {
      return (
        <div className="content-area">
          <div className="company-detail">
            <div className="company-header">
              {selectedCompany.logo && (
                <div className="company-avatar-large">
                  <img 
                    src={selectedCompany.logo} 
                    alt={`${selectedCompany.name} logo`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <div className="company-info">
                <h1 className="company-name">{selectedCompany.name}</h1>
                <div className="company-meta">
                  <div className="company-meta-left">
                    <span className="company-ticker-label">Ticker: <span className="company-ticker-value">${selectedCompany.ticker}</span></span>
                    {selectedCompany.twitter && (
                      <a 
                        href={selectedCompany.twitter} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="company-twitter"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                        @{selectedCompany.twitter.split('/').pop()}
                      </a>
                    )}
                    {selectedCompany.website && (
                      <a 
                        href={selectedCompany.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="company-website"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="2" y1="12" x2="22" y2="12"/>
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                        </svg>
                        {selectedCompany.website.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
                      </a>
                    )}
                  </div>
                  <span className={`disclosure-badge ${selectedCompany.onchainWalletsDisclosed?.toLowerCase() === 'yes' ? 'disclosed' : 'not-disclosed'}`}>
                    <span className="disclosure-dot"></span>
                    Onchain Wallets Disclosed: {selectedCompany.onchainWalletsDisclosed || 'No'}
                  </span>
                </div>
                
                {/* Company-specific Notes */}
                {selectedCompany.ticker === 'MARA' && (
                  <div className="company-note-inline">
                    <strong>Note:</strong> Excludes self-mined BTC; only balance sheet purchases and sales included.
                  </div>
                )}
              </div>
            </div>
            
            {/* Company-specific charts and data */}
            <CompanyDetail 
              company={selectedCompany} 
              accentColor={tabs.find(t => t.id === activeTab)?.accentColor}
              assetType={activeTab === 'bitcoin' ? 'BTC' : activeTab === 'ethereum' ? 'ETH' : activeTab === 'solana' ? 'SOL' : null}
            />
          </div>
          <Footer />
        </div>
      );
    }
    
    return null;
  };

  const renderTabContent = () => {
    // Don't show sidebar for overview tab
    if (activeTab === 'overview') {
      return renderContent();
    }

    // Show sidebar for crypto tabs (bitcoin, ethereum, solana)
    return (
      <div className="tab-content">
        <Sidebar 
          cryptoType={activeTab}
          activeView={activeView}
          onViewChange={handleViewChange}
        />
        {renderContent()}
      </div>
    );
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Handle scroll to hide/show header with throttling for smooth performance
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const contentArea = document.querySelector('.content-area');
          if (contentArea) {
            const scrollTop = contentArea.scrollTop;
            setHeaderScrolled(scrollTop > 50);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    const contentArea = document.querySelector('.content-area');
    if (contentArea) {
      contentArea.addEventListener('scroll', handleScroll, { passive: true });
      return () => contentArea.removeEventListener('scroll', handleScroll);
    }
  }, [activeTab, activeView]);

  return (
    <div className={`landing-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`} data-crypto={activeTab}>
      <header className={`main-header ${headerScrolled ? 'scrolled' : ''}`}>
        <div className="header-center">
          <h1 className="app-title">Digital Asset Treasury Board</h1>
          <p className="app-subtitle">
            <span className="powered-by-text">powered by </span>
            <a 
              href="https://panteraresearchlab.xyz/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="pantera-link"
            >
              <img 
                src={isDarkMode ? '/images/dark_mode_icon.png' : '/images/light_mode_icon.png'} 
                alt="Pantera Research Lab" 
                className="pantera-logo-subtitle"
              />
              <span className="pantera-text">Pantera Research Lab</span>
            </a>
          </p>
        </div>
      </header>

      <nav className="crypto-tabs">
        <div className="tabs-container">
          <div className="tabs-left">
            <a 
              href="https://panteraresearchlab.xyz/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="tabs-logo-link"
            >
              <img 
                src={isDarkMode ? '/images/dark_mode_icon.png' : '/images/light_mode_icon.png'} 
                alt="Pantera Research Lab" 
                className="tabs-solana-logo tabs-solana-logo-main"
              />
              <img 
                src={isDarkMode ? '/images/dark_mode_icon.png' : '/images/light_mode_icon.png'} 
                alt="Pantera Research Lab" 
                className="tabs-solana-logo tabs-solana-logo-vertical"
              />
            </a>
          </div>
          
          <div className="tabs-center">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
                style={{
                  '--tab-color': tab.color,
                  borderBottomColor: activeTab === tab.id ? tab.color : 'transparent'
                }}
              >
                <span className="tab-label-desktop">
                  <span className="tab-first-letter">{tab.label.charAt(0)}</span>
                  <span className="tab-rest-text">{tab.label.slice(1)}</span>
                </span>
                {tab.id === 'overview' ? (
                  <span className="tab-label-mobile tab-home-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                      <polyline points="9 22 9 12 15 12 15 22"/>
                    </svg>
                  </span>
                ) : (
                  <span className="tab-label-mobile">{tab.mobileLabel}</span>
                )}
              </button>
            ))}
          </div>
          
          <div className="tabs-right">
            <a 
              href="https://panteraresearchlab.xyz/decoding-dats-beyond-mnav/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="report-link"
              aria-label="View Report"
            >
              <svg className="scroll-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 3C6 2.44772 6.44772 2 7 2H17C17.5523 2 18 2.44772 18 3V19C18 19.5523 17.5523 20 17 20H7C6.44772 20 6 19.5523 6 19V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 3C6 2.44772 6.44772 2 7 2C7.55228 2 8 2.44772 8 3V5C8 5.55228 7.55228 6 7 6C6.44772 6 6 5.55228 6 5V3Z" fill="currentColor"/>
                <path d="M16 3C16 2.44772 16.4477 2 17 2C17.5523 2 18 2.44772 18 3V5C18 5.55228 17.5523 6 17 6C16.4477 6 16 5.55228 16 5V3Z" fill="currentColor"/>
                <line x1="8" y1="8" x2="16" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="8" y1="11" x2="16" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="8" y1="14" x2="14" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span className="report-link-text">Report</span>
            </a>
            <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
              {isDarkMode ? (
                <svg className="theme-icon sun-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="5" fill="currentColor"/>
                  <line x1="12" y1="1" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="12" y1="21" x2="12" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="1" y1="12" x2="3" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="21" y1="12" x2="23" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg className="theme-icon moon-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" fill="currentColor"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </nav>

      <main className="main-content">
        {renderTabContent()}
      </main>
    </div>
  );
};

export default LandingPage;
